import { useMemo } from 'react'
import {
    Building2,
    FileText,
    Gavel,
    Globe,
    Landmark,
    LayoutDashboard,
    Scale,
    Users,
    ExternalLink,
    Briefcase,
    CreditCard
} from 'lucide-react'

interface ServiceLink {
    id: string
    title: string
    description: string
    url: string
    icon: any
    highlight?: boolean
}

interface ServiceCategory {
    id: string
    title: string
    description: string
    icon: any
    color: string
    services: ServiceLink[]
}

export function EServicesPage({ lang }: { lang: 'fr' | 'ar' }) {

    const categories: ServiceCategory[] = useMemo(() => [
        {
            id: 'tax',
            title: lang === 'ar' ? 'الضرائب والمالية' : 'Impôts & Finances',
            description: lang === 'ar' ? 'بوابة المديرية العامة للضرائب' : 'Direction Générale des Impôts',
            icon: CreditCard,
            color: 'blue',
            services: [
                {
                    id: 'simpl-is',
                    title: lang === 'ar' ? 'الضريبة على الشركات (IS)' : 'SIMPL-IS (Sociétés)',
                    description: lang === 'ar' ? 'التصريح والأداء للشركات' : 'Déclaration et paiement IS',
                    url: 'https://is.tax.gov.ma',
                    icon: Building2,
                    highlight: true
                },
                {
                    id: 'simpl-ir',
                    title: lang === 'ar' ? 'الضريبة على الدخل (IR)' : 'SIMPL-IR (Revenu)',
                    description: lang === 'ar' ? 'التصريح بالدخل للمهنيين' : 'Déclaration IR Professionnels',
                    url: 'https://ir.tax.gov.ma',
                    icon: Users
                },
                {
                    id: 'simpl-tva',
                    title: lang === 'ar' ? 'الضريبة على القيمة المضافة (TVA)' : 'SIMPL-TVA',
                    description: lang === 'ar' ? 'التصريح بالضريبة على القيمة المضافة' : 'Déclaration TVA',
                    url: 'https://tva.tax.gov.ma',
                    icon: FileText
                },
                {
                    id: 'simpl-adh',
                    title: lang === 'ar' ? 'الانخراط (Simpl-Adhésion)' : 'Simpl-Adhésion',
                    description: lang === 'ar' ? 'تدبير الانخراط في الخدمات الإلكترونية' : 'Gestion des adhésions en ligne',
                    url: 'https://adhesion.tax.gov.ma',
                    icon: Users
                },
                {
                    id: 'vignette',
                    title: lang === 'ar' ? 'الضريبة على السيارات' : 'Vignette (TSAV)',
                    description: lang === 'ar' ? 'أداء الضريبة السنوية على السيارات' : 'Paiement de la Vignette',
                    url: 'https://vignette.ma',
                    icon: CreditCard
                },
                {
                    id: 'tax-portal',
                    title: lang === 'ar' ? 'بوابة الضرائب' : 'Portail DGI',
                    description: lang === 'ar' ? 'الموقع الرسمي للمديرية العامة للضرائب' : 'Site officiel de la DGI',
                    url: 'https://www.tax.gov.ma',
                    icon: Globe
                }
            ]
        },
        {
            id: 'justice',
            title: lang === 'ar' ? 'العدل وفضاء المحاكم' : 'Justice & Tribunaux',
            description: lang === 'ar' ? 'الخدمات القضائية الإلكترونية' : 'Services judiciaires électroniques',
            icon: Scale,
            color: 'emerald',
            services: [
                {
                    id: 'mahakim',
                    title: lang === 'ar' ? 'محاكم (تتبع الملفات)' : 'Mahakim (Suivi de dossiers)',
                    description: lang === 'ar' ? 'تتبع الملفات وجداول الجلسات' : 'Suivi des affaires en ligne',
                    url: 'https://www.mahakim.ma/#/suivi/dossier-suivi',
                    icon: Gavel,
                    highlight: true
                },
                {
                    id: 'rc',
                    title: lang === 'ar' ? 'السجل التجاري' : 'Registre de Commerce',
                    description: lang === 'ar' ? 'خدمات السجل التجاري' : 'Services du Registre de Commerce',
                    url: 'https://servicesenligne.justice.gov.ma/',
                    icon: Building2
                },
                {
                    id: 'casier',
                    title: lang === 'ar' ? 'السجل العدلي' : 'Casier Judiciaire',
                    description: lang === 'ar' ? 'طلب السجل العدلي عبر الإنترنت' : 'Demande de casier judiciaire',
                    url: 'https://casierjudiciaire.justice.gov.ma/',
                    icon: FileText
                },
                {
                    id: 'amendes',
                    title: lang === 'ar' ? 'أداء الغرامات' : 'Paiement des Amendes',
                    description: lang === 'ar' ? 'أداء غرامات السير والمخالفات' : 'Paiement des amendes et infractions',
                    url: 'https://amendes.justice.gov.ma/',
                    icon: CreditCard
                },
                {
                    id: 'rnesm',
                    title: lang === 'ar' ? 'السجل الوطني للضمانات' : 'Sûretés Mobilières',
                    description: lang === 'ar' ? 'السجل الوطني الإلكتروني للضمانات المنقولة' : 'Registre National Électronique des Sûretés Mobilières',
                    url: 'https://rnesm.justice.gov.ma/',
                    icon: Landmark
                },
                {
                    id: 'vente',
                    title: lang === 'ar' ? 'البيوعات القضائية' : 'Ventes Judiciaires',
                    description: lang === 'ar' ? 'إعلانات البيوعات العقارية والمنقولة' : 'Annonces des ventes immobilières',
                    url: 'https://www.mahakim.ma/#/vente',
                    icon: Scale
                }
            ]
        },
        {
            id: 'business',
            title: lang === 'ar' ? 'المقاولات والأعمال' : 'Entreprises & Business',
            description: lang === 'ar' ? 'إنشاء وتدبير المقاولات' : 'Création et gestion d\'entreprises',
            icon: Briefcase,
            color: 'amber',
            services: [
                {
                    id: 'directinfo',
                    title: lang === 'ar' ? 'DirectInfo (OMPIC)' : 'DirectInfo (OMPIC)',
                    description: lang === 'ar' ? 'المعلومات القانونية والمالية للمقاولات' : 'Info légale et financière',
                    url: 'https://www.directinfo.ma',
                    icon: Building2,
                    highlight: true
                },
                {
                    id: 'create-ent',
                    title: lang === 'ar' ? 'إنشاء المقاولة' : 'Création d\'entreprise',
                    description: lang === 'ar' ? 'الشباك الوحيد لإنشاء المقاولات' : 'Guichet unique électronique',
                    url: 'https://www.directentreprise.ma',
                    icon: LayoutDashboard
                },
                {
                    id: 'markets',
                    title: lang === 'ar' ? 'الصفقات العمومية' : 'Marchés Publics',
                    description: lang === 'ar' ? 'بوابة الصفقات العمومية' : 'Portail des marchés publics',
                    url: 'https://www.marchespublics.gov.ma',
                    icon: FileText
                }
            ]
        },
        {
            id: 'social',
            title: lang === 'ar' ? 'الضمان الاجتماعي' : 'Sécurité Sociale',
            description: lang === 'ar' ? 'بوابة الصندوق الوطني للضمان الاجتماعي' : 'Portail CNSS',
            icon: Users,
            color: 'indigo',
            services: [
                {
                    id: 'damancom',
                    title: lang === 'ar' ? 'ضمانكم (للمشغلين)' : 'Damancom (Entreprises)',
                    description: lang === 'ar' ? 'التصريح بالأجراء وأداء الاشتراكات' : 'Déclarations et cotisations',
                    url: 'https://www.damancom.ma',
                    icon: Users,
                    highlight: true
                },
                {
                    id: 'macnss',
                    title: lang === 'ar' ? 'بوابتي (للأجراء)' : 'Ma CNSS (Assurés)',
                    description: lang === 'ar' ? 'تتبع التعويضات والتصريحات' : 'Suivi des remboursements',
                    url: 'https://www.macnss.ma',
                    icon: Users
                }
            ]
        },
        {
            id: 'land',
            title: lang === 'ar' ? 'المحافظة العقارية' : 'Conservation Foncière',
            description: lang === 'ar' ? 'خدمات الوكالة الوطنية للمحافظة العقارية' : 'Services de l\'ANCFCC',
            icon: Landmark,
            color: 'purple',
            services: [
                {
                    id: 'mohafadati',
                    title: lang === 'ar' ? 'محافظتي' : 'Mohafadati',
                    description: lang === 'ar' ? 'تتبع الرسوم العقارية' : 'Suivi des titres fonciers',
                    url: 'https://www.ancfcc.gov.ma/Mohafadati',
                    icon: Landmark,
                    highlight: true
                },
                {
                    id: 'cadastre',
                    title: lang === 'ar' ? 'تصاميم الكاداستر' : 'Cadastre',
                    description: lang === 'ar' ? 'طلب التصاميم العقارية' : 'Demande de plans cadastraux',
                    url: 'https://www.ancfcc.gov.ma',
                    icon: Globe
                }
            ]
        },
        {
            id: 'admin',
            title: lang === 'ar' ? 'إدارتي والإجراءات' : 'Administration & Procédures',
            description: lang === 'ar' ? 'بوابة الإجراءات الإدارية الموحدة' : 'Portail unifié des procédures',
            icon: Globe,
            color: 'slate',
            services: [
                {
                    id: 'idarati',
                    title: lang === 'ar' ? 'إدارتي' : 'Idarati',
                    description: lang === 'ar' ? 'دليل المساطر والإجراءات الإدارية' : 'Guide des procédures administratives',
                    url: 'https://www.idarati.ma',
                    icon: Globe,
                    highlight: true
                },
                {
                    id: 'wraqi',
                    title: lang === 'ar' ? 'وثيقتي' : 'Wraqi',
                    description: lang === 'ar' ? 'المصادقة الإلكترونية على الوثائق' : 'Légalisation digiitale',
                    url: 'https://www.wraqi.ma',
                    icon: FileText
                },
                {
                    id: 'chikaya',
                    title: lang === 'ar' ? 'شكاية' : 'Chikaya',
                    description: lang === 'ar' ? 'البوابة الوطنية للشكايات' : 'Portail national des réclamations',
                    url: 'https://www.chikaya.ma',
                    icon: Scale
                }
            ]
        }
    ], [lang])

    return (
        <div className="flex h-full flex-col overflow-y-auto bg-slate-50">
            <div className="mx-auto w-full max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
                <div className="mb-8">
                    <h1 className="text-2xl font-bold text-slate-900 sm:text-3xl">
                        {lang === 'ar' ? 'بوابة الخدمات الإلكترونية' : 'Portail E-Services'}
                    </h1>
                    <p className="mt-2 text-slate-600">
                        {lang === 'ar'
                            ? 'الوصول المباشر إلى الخدمات الرقمية الرسمية للإدارات المغربية'
                            : 'Accès direct aux services numériques officiels de l\'administration marocaine'}
                    </p>
                </div>

                <div className="grid gap-8 pb-12">
                    {categories.map((category) => {
                        const Icon = category.icon
                        const colorContext = {
                            blue: 'bg-blue-50 text-blue-700 ring-blue-600/20',
                            emerald: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20',
                            amber: 'bg-amber-50 text-amber-700 ring-amber-600/20',
                            indigo: 'bg-indigo-50 text-indigo-700 ring-indigo-600/20',
                            purple: 'bg-purple-50 text-purple-700 ring-purple-600/20',
                            slate: 'bg-slate-100 text-slate-700 ring-slate-600/20',
                        }[category.color] || 'bg-slate-50 text-slate-700 ring-slate-600/20'

                        return (
                            <section key={category.id} className="scroll-mt-8">
                                <div className="mb-4 flex items-center gap-3">
                                    <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${colorContext.split(' ')[0]} ${colorContext.split(' ')[1]}`}>
                                        <Icon className="h-6 w-6" />
                                    </div>
                                    <div>
                                        <h2 className="text-lg font-semibold text-slate-900">{category.title}</h2>
                                        <p className="text-sm text-slate-500">{category.description}</p>
                                    </div>
                                </div>

                                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                                    {category.services.map((service) => {
                                        const ServiceIcon = service.icon
                                        return (
                                            <a
                                                key={service.id}
                                                href={service.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className={`group relative flex flex-col gap-3 rounded-xl border p-4 shadow-sm transition-all hover:shadow-md ${service.highlight
                                                    ? 'border-blue-200 bg-white hover:border-blue-300'
                                                    : 'border-slate-200 bg-white hover:border-slate-300'
                                                    }`}
                                            >
                                                <div className="flex items-start justify-between">
                                                    <div className={`rounded-lg p-2 ${service.highlight ? 'bg-blue-50 text-blue-600' : 'bg-slate-50 text-slate-500'}`}>
                                                        <ServiceIcon className="h-5 w-5" />
                                                    </div>
                                                    <ExternalLink className="h-4 w-4 text-slate-300 transition-colors group-hover:text-blue-500" />
                                                </div>

                                                <div>
                                                    <h3 className="font-semibold text-slate-900 group-hover:text-blue-600">
                                                        {service.title}
                                                    </h3>
                                                    <p className="mt-1 text-sm text-slate-500 line-clamp-2">
                                                        {service.description}
                                                    </p>
                                                </div>
                                            </a>
                                        )
                                    })}
                                </div>
                            </section>
                        )
                    })}
                </div>
            </div>
        </div>
    )
}
